import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useAppSelector from '@fogia/core-web/hooks/useAppSelector';
import { stormSrc } from '@fogia/core-web/libs/image';
import decodeHTMLEntities from '@fogia/core-web/utils/decodeHTMLEntities';
import getCountrySpecificVariable from '@fogia/core-web/utils/getCountrySpecificVariable';
import { getMediaUrl } from '@fogia/core-web/utils/getMediaUrl';
import { getHreflangs, getLanguages } from '@fogia/core-web/utils/multimarket';
import { getParametric } from '@fogia/core-web/utils/storm';
import LanguageLinks from '@fogia/core-web/components/Meta/LanguageLinks';
import ProductStructuredData from '@fogia/core-web/components/metadata/structuredData/ProductStructuredData';
import formatMetaDescription from '@fogia/core-web/components/metadata/utils/formatMetaDescription';

const getImagesFromFiles = (files) =>
    files.reduce((acc, cur) => {
        const { format } = cur;

        if (format.image) {
            return [...acc, getMediaUrl(format.image)];
        }

        return acc;
    }, []);

const getValidMetaDescription = (description) => {
    let strippedDescription = description;
    for (let i = 0; i < 3; i++) {
        if (strippedDescription.length > 155) {
            strippedDescription = strippedDescription.split('✓').slice(0, -1).join('✓');
        }
    }

    return strippedDescription;
};

const ProductMeta = ({ product, colorParametric }) => {
    const { t } = useTranslation();

    const site = useAppSelector(({ application }) => application.site) || {};
    const localeAlternates = useAppSelector(({ page }) => page.localeAlternates);
    const resolvedSlug = useAppSelector(({ page }) => page?.resolvedSlug ?? '');

    if (!product) {
        return null;
    }

    const { protocol, domain } = site;

    const { eanCode, format, files = [], manufacturer, partNo, categories } = product;
    const imageSrc = (format || {}).image || (files[0] && files[0].format.image);
    const mpn = manufacturer?.partNo || null;
    const material = product.material || null; // TODO: Add material to product
    const sku = partNo;
    const ean = eanCode;
    const brand = manufacturer?.name || manufacturer?.uniqueName;
    const isUsedProduct = categories?.some((category) => category.id === 74043);

    const { includedProducts } = product;

    const images = getImagesFromFiles(Array.isArray(product.files) ? product.files : []);
    if (imageSrc) images.unshift(stormSrc(imageSrc, { m: 'pad', auto: 'format' }, true));

    const seoProductTitle = getParametric('title_seo', product)?.value;
    const productName = product.name || product.fullName;
    const titleFallback = t('seo.product_title_fallback', {
        product: productName || '',
    });

    const productMetaDescription = product.productDescriptionLong || product.productDescriptionShort;

    const metaDescriptionFallback = getValidMetaDescription(t('seo.product_description_fallback'));

    const title = seoProductTitle || titleFallback;
    const metaDescription = productMetaDescription || metaDescriptionFallback;

    const productUrl = resolvedSlug && domain ? `${protocol || ''}${domain}${resolvedSlug}` : '';

    const keywords = product.keywords || null;

    const ogType = 'product';
    const ogTitle = title;
    const ogDescription = metaDescription || null;
    const ogImage = imageSrc;
    const ogUrl = productUrl || null;
    const ogLocale = site.locale || null;
    const ogProductAvailability = 'available for order';
    const facebookAppId = process.env.REACT_APP_FACEBOOK_APP_ID;
    const facebookDomainVerification = getCountrySpecificVariable('REACT_APP_FACEBOOK_DOMAIN_VERIFICATION', ogLocale);

    let hreflangs;

    // TODO: remove this once we have a way to get the correct localeAlternates for upcoming B2E sites
    const filteredLocaleAlternates = localeAlternates.filter(
        (localeAlternate) => localeAlternate.applicationId === site.applicationId,
    );

    if (filteredLocaleAlternates) {
        const languages = getLanguages(site.languages, ogLocale);
        hreflangs = getHreflangs(filteredLocaleAlternates, languages);
    }

    return (
        <>
            <Helmet>
                {title && <title>{decodeHTMLEntities(title)}</title>}
                {productUrl && <link href={productUrl} rel="canonical" />}
                {metaDescription && (
                    <meta name="description" content={formatMetaDescription(decodeHTMLEntities(metaDescription))} />
                )}
                {keywords && <meta name="keywords" content={keywords} />}

                {ogType && <meta property="og:type" content={ogType} />}
                {ogTitle && <meta property="og:title" content={ogTitle} />}
                {ogDescription && <meta property="og:description" content={formatMetaDescription(ogDescription)} />}
                {ogImage && <meta property="og:image" content={ogImage} />}
                {ogUrl && <meta property="og:url" content={ogUrl} />}
                {ogLocale && <meta property="og:locale" content={ogLocale} />}
                {brand && <meta property="product:brand" content={brand} />}
                {ogProductAvailability && <meta property="product:availability" content={ogProductAvailability} />}
                <meta property="product:condition" content="new" />
                {ogLocale && <meta property="product:locale" content={ogLocale} />}
                {facebookAppId && <meta property="fb:app_id" content={facebookAppId} />}
                {facebookDomainVerification && (
                    <meta name="facebook-domain-verification" content={facebookDomainVerification} />
                )}
            </Helmet>
            <ProductStructuredData
                brand={brand}
                categories={product.categories}
                description={metaDescription}
                ean={ean}
                images={images}
                includedProducts={includedProducts}
                productAvailability="available for order"
                mpn={mpn}
                name={product.name || product.fullName}
                sku={sku}
                url={productUrl}
                variants={product.variants || []}
                averageScore={product.reviewsData?.averageScore}
                totalReviews={product.reviewsData?.totalReviews}
                isUsedProduct={isUsedProduct}
                statusId={product.statusId}
                color={colorParametric}
                material={material}
                product={product}
            />
            {hreflangs && <LanguageLinks links={hreflangs} />}
        </>
    );
};

ProductMeta.propTypes = {
    product: PropTypes.object.isRequired,
};

export default React.memo(ProductMeta, (prevProps, nextProps) => prevProps.product.partNo === nextProps.product.partNo);
